import { Injectable } from '@angular/core';
import { BehaviorSubject, take } from 'rxjs';
import { ItemUpdate } from '@weavix/models/src/badges/event';
import { SelectionOptions } from '@weavix/models/src/folder/folder';
import { Resource } from '../models/folderized-data.model';
import { AnyItem, ItemType, ItemTypeFacilities } from '../models/item.model';
import { Topic } from '@weavix/models/src/topic/topic';
import { Utils } from '../utils/utils';
import { AccountService } from './account.service';
import { AlertService } from './alert.service';
import { CacheContext, HttpService } from './http.service';
import { PubSubService } from './pub-sub.service';
import { TranslationService } from './translation.service';
import { WorkFormSubmission } from '@weavix/models/src/work-forms/work-forms';
import { colorPalette } from '@weavix/domain/src/utils/colors';

// A color from the /colors endpoint
const defaultIconColor: string = '#17202a';

@Injectable({
    providedIn: 'root',
})
export class ItemService {

    constructor(
        private readonly httpService: HttpService,
        private readonly pubSubService: PubSubService,
        private readonly accountService: AccountService,
        private readonly alertsService: AlertService,
        private readonly translationService: TranslationService,
    ) { }

    static readonly ACTIVE_DYNAMIC_ITEM_TIME_MINUTES = 30;
    static itemTypesCacheCollection = 'ItemTypes';
    static itemsCacheCollection = 'Items';
    private static readonly itemTypeCacheContext: CacheContext = { collection: ItemService?.itemTypesCacheCollection, maxAge: 1_800_000 };
    private static readonly itemCacheContext: CacheContext = { collection: ItemService?.itemsCacheCollection, maxAge: 1_800_000 };

    static baseItemsUrl = '/core/items';
    static baseItemTypesUrl = '/core/item-types';

    private icons$: BehaviorSubject<string[]>;

    itemTypeMap: { [key: string]: ItemType } = {};
    static itemsUrl = (id?: string) => id ? `${ItemService.baseItemsUrl}/${id}` : ItemService.baseItemsUrl;
    static itemTypesUrl = (id?: string) => id ? `${ItemService.baseItemTypesUrl}/${id}` : ItemService.baseItemTypesUrl;

    public static clearItemsTypeCache = () => HttpService.clearCache(ItemService.itemTypeCacheContext.collection);
    public static clearItemCache = () => HttpService.clearCache(ItemService.itemCacheContext.collection);

    getItem(component: any, id: string) {
        return this.httpService.get<AnyItem>(component, ItemService.itemsUrl(id), null, ItemService.itemCacheContext);
    }

    getItems(component: any, facilityId?: string, mapItem?: boolean, tags?: string[], getCached: boolean = true) {
        return this.httpService.get<AnyItem[]>(component, ItemService.itemsUrl(), { facilityId, tags, mapItem }, getCached ? ItemService.itemCacheContext : null);
    }

    async getItemResources(component, selectionOptions?: SelectionOptions, facilityId?: string): Promise<Resource[]> {
        try {
            let resources: Resource[] = [];
            resources = (await this.getItems(component, facilityId)).map(i => this.makeResource(i as AnyItem, selectionOptions ? selectionOptions : { ids: true }));
            return Utils.sortAlphabetical(resources, (item) => item.displayText);
        } catch (e) {
            this.alertsService.sendError({ error: e, messageKey: 'ERRORS.ITEMS.GET' });
            return [];
        }
    }

    makeResource(i: AnyItem, selectionOptions: SelectionOptions): Resource {
        return {
            displayText: i.name,
            key: i.id,
            data: i,
            parentId: i.folderId || i.facilityId || null,
            isSelectable: !!selectionOptions.ids,
        };
    }

    getAccountOnlyItems(component: any, tags?: string[]) {
        return this.httpService.get<AnyItem[]>(component, ItemService.itemsUrl(), { facilityId: 'root', tags }, ItemService.itemCacheContext);
    }

    createItem(component: any, item: AnyItem) {
        ItemService.clearItemsTypeCache();
        return this.httpService.post<AnyItem>(component, ItemService.itemsUrl(), item, ItemService.itemCacheContext);
    }

    updateItem(component: any, id: string, item: AnyItem) {
        return this.httpService.put<AnyItem>(component, ItemService.itemsUrl(id), item, ItemService.itemCacheContext);
    }

    deleteItem(component: any, id: string) {
        ItemService.clearItemsTypeCache();
        return this.httpService.delete<AnyItem>(component, ItemService.itemsUrl(id), null, ItemService.itemCacheContext);
    }

    getFormSubmissions(component: any, id: string) {
        return this.httpService.get<WorkFormSubmission[]>(component, `${ItemService.itemsUrl(id)}/submissions`, null, ItemService.itemCacheContext);
    }

    async getAllTypes(component: any, tags?: string[], getCached: boolean = true) {
        let types = await (this.httpService.get<ItemType[]>(component, ItemService.itemTypesUrl(), { tags }, getCached ? ItemService.itemTypeCacheContext : null));
        types = types.map(t => (this.checkToUpdateDefaultTypes(t)));

        return types;
    }

    async getAllTypesWithCorrespondingFacilities(component) {
        let types = await (this.httpService.get<ItemTypeFacilities[]>(component, `${ItemService.itemTypesUrl()}/facilities`, {}, null));
        types = types.map(t => (this.checkToUpdateDefaultTypes(t)));

        return types;
    }

    async getItemTypeFacilityIds(component: any, id: string) {
        return await (this.httpService.get<string[]>(component, `${ItemService.itemTypesUrl()}/${id}/facilities`));
    }

    async buildItemTypeMap(component) {
        const lists = await this.getAllTypes(component);
        this.itemTypeMap = lists.reduce((a, b) => { a[b.id] = b; return a; }, {});
    }

    async getType(component: any, id: string) {
        const type = await (this.httpService.get<ItemType>(component, ItemService.itemTypesUrl(id), null, ItemService.itemTypeCacheContext));
        return this.checkToUpdateDefaultTypes(type);
    }

    async createType(component: any, type: ItemType) {
        const result = await this.httpService.post<ItemType>(component, ItemService.itemTypesUrl(), type, ItemService.itemTypeCacheContext);
        this.itemTypeMap[result.id] = result;
        return result;
    }

    async updateType(component: any, id: string, type: ItemType) {
        const result = await this.httpService.put<ItemType>(component, ItemService.itemTypesUrl(id), type, ItemService.itemTypeCacheContext);
        this.itemTypeMap[result.id] = result;
        return result;
    }

    async deleteType(component: any, id: string) {
        const result = await this.httpService.delete<ItemType>(component, ItemService.itemTypesUrl(id), null, ItemService.itemTypeCacheContext);
        delete this.itemTypeMap[result.id];
        return result;
    }

    async subscribeUpdates(c: any) {
        return (await this.pubSubService.subscribe<ItemUpdate>(c, Topic.AccountItems, [this.accountService.getAccountId()]));
    }

    async icons(component: any) {
        if (!this.icons$) {
            this.icons$ = new BehaviorSubject(await this.httpService.get<string[]>(component, `${ItemService.itemTypesUrl()}/icons`));
        }
        return await this.icons$.pipe(take(1)).toPromise();
    }

    colors() {
        return colorPalette;
    }

    defaultIconColor() {
        return defaultIconColor;
    }

    private checkToUpdateDefaultTypes<T extends ItemType>(type: T): T {
        if (!type.isDefault) return type;

        const updatedType = {
            ...type,
            name: this.translationService.getImmediate(`items.types.defaults.${type.id}`),
        };

        return updatedType as T;
    }
}
